import { CSSProperties, useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, Chip, ClickAwayListener, Tooltip, SxProps, Theme, Typography } from '@mui/material';

const QUESTION_HEADER_STRINGS = appStrings.modules.questionnaire;

type ToolTipItemProps = Readonly<{
  title: string;
  tooltipText: string;
  tooltipCount: number;
}>;

const ToolTipItem = ({ title, tooltipText, tooltipCount }: ToolTipItemProps) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  return (
    <Box key={title} sx={tooltipItemSx(tooltipCount)}>
      <Typography
        variant="text-xs-semibold"
        color={({ backrs }) => backrs.colors['Colors/Text/text-quaternary']}
      >
        {title}
      </Typography>

      <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
        <Tooltip
          open={isTooltipOpen}
          onClose={() => setIsTooltipOpen(false)}
          title={tooltipText}
          placement="top"
          arrow
          disableFocusListener
          disableHoverListener
          disableTouchListener
          componentsProps={{
            tooltip: {
              sx: toolTipComponentSx,
            },
          }}
        >
          <Box onClick={() => setIsTooltipOpen((prev) => !prev)} sx={iconContainerSx}>
            <AppIcon iconName="HelpCircle" diameter="16px" color="Colors/Foreground/fg-quinary" />
          </Box>
        </Tooltip>
      </ClickAwayListener>
    </Box>
  );
};

export type QuestionHeaderProps = Readonly<{
  title: string;
  subCategory: string;
  description: string;
  tooltips: {
    title: string;
    tooltip: string;
  }[];
  showChip?: boolean;
}>;

export const QuestionHeader = ({
  title,
  subCategory,
  description,
  tooltips,
  showChip = false,
}: QuestionHeaderProps) => {
  return (
    <Box sx={containerSx}>
      <Box sx={titleContainerSx}>
        <Box sx={titleSubCategoryContainerSx}>
          <Typography
            variant="text-lg-medium"
            component="div"
            color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
          >
            {title}
          </Typography>
          {showChip && <Chip variant="pill" size="sm" label={subCategory} sx={chipSx} />}
        </Box>
        <Typography
          variant="text-sm-regular"
          color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
        >
          {description.replace(/--/g, '—')}
        </Typography>
      </Box>

      <Box sx={tableHeaderContainerSx}>
        <Typography variant="text-xs-semibold" component="div" sx={firstColumnHeaderSx}>
          {QUESTION_HEADER_STRINGS.facet}
        </Typography>

        <Box sx={toolTipContainerSx}>
          {tooltips.map((tooltip) => (
            <ToolTipItem
              key={tooltip.title}
              title={tooltip.title}
              tooltipText={tooltip.tooltip}
              tooltipCount={tooltips.length}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
};

const titleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  py: backrs.spacing['spacing-2xl'],
  px: {
    xs: backrs.spacing['spacing-xl'],
    md: backrs.spacing['spacing-3xl'],
  },
  display: 'flex',
  flexDirection: 'column',
});

const toolTipContainerSx: SxProps<Theme> = {
  width: '75%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  transition: 'all 0.3s ease-in-out',
};

const toolTipComponentSx: CSSProperties = {
  whiteSpace: 'pre-line',
  padding: '16px',
  fontFamily: 'Sarabun, sans-serif',
};

const tooltipItemSx =
  (numTooltips: number): SxProps<Theme> =>
  ({ backrs }) => ({
    width: `calc(100% / ${numTooltips})`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    px: {
      xs: backrs.spacing['spacing-xl'],
      md: backrs.spacing['spacing-3xl'],
    },
    gap: backrs.spacing['spacing-xs'],
    transition: 'all 0.3s ease-in-out',
  });

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Brand/50'],
  borderColor: backrs.colors['Brand/200'],
  color: backrs.colors['Brand/700'],
});

const titleSubCategoryContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const tableHeaderContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  borderTop: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const firstColumnHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
  width: '25%',
  py: backrs.spacing['spacing-lg'],
  px: backrs.spacing['spacing-3xl'],
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const iconContainerSx: SxProps<Theme> = {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
